<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import type { ColumnConfig } from "@types"
import type { FolderForDialog } from "@types"
import type { Services } from "@services"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"
import {
    collectSubtreeIds,
    filterFolderTreeByIds,
    normalizeFolderId,
    WORKSPACE_ROOT_TREE_KEY,
    wrapFolderTreeWithWorkspaceRoot,
    type FolderMoveTreeNode,
} from "@modules/home/components/dashboard/move-to-folder/homeMoveToFolderDialog.utils"

defineOptions({
    name: "HomeMoveToFolderDialog",
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Affichage du dialogue. */
        visible: boolean
        /** Déplacement de bâtiments (sous-arbre) ou d’un dossier. */
        mode: "building" | "folder"
        /** Bâtiments à déplacer (y compris enfants du nœud racine). */
        buildingIds?: string[]
        /** Dossier courant du bâtiment avant déplacement (racine = null). */
        sourceFolderId?: string | null
        /** Dossier à déplacer (mode dossier). */
        fromFolderId?: string
        /**
         * Parent actuel du dossier déplacé (déduit de la route / liste).
         * Sert à détecter un déplacement sans effet.
         */
        sourceParentFolderId?: string | null
    }>(),
    {
        buildingIds: () => [],
        sourceFolderId: null,
        fromFolderId: undefined,
        sourceParentFolderId: null,
    }
)

const emit = defineEmits<{
    "update:visible": [value: boolean]
}>()

// ================================
// COMPOSABLES
// ================================
const { $getFolderTree } = inject<Services>("services")!
const homeWorkspace = useHomeWorkspaceStore()

// ================================
// STATE
// ================================
/** Arbre dossiers brut (API). */
const rawFolderTree = ref<FolderForDialog[]>([])
/** Chargement de l’arbre. */
const loadingTree = ref(false)
/** Soumission du déplacement. */
const loadingMove = ref(false)
/** Ligne sélectionnée dans le TreeTable (mode single). */
const selectedRows = ref<FolderMoveTreeNode[]>([])
/** Nœud « Mon espace » développé par défaut pour afficher les dossiers racine. */
const treeExpandedKeys = ref<Record<string, boolean>>({
    [WORKSPACE_ROOT_TREE_KEY]: true,
})

// ================================
// COMPUTED
// ================================
const getDialogHeader = computed(() =>
    props.mode === "building" ? "Déplacer le bâtiment" : "Déplacer le dossier"
)

const getTableColumns = computed<ColumnConfig[]>(() => [
    {
        field: "folder",
        header: "Dossier",
        sortable: true,
        style: { minWidth: "16rem" },
        expander: true,
    },
    {
        field: "building",
        header: "Bâtiments",
        sortable: true,
        style: { width: "8rem" },
        expander: false,
    },
])

/**
 * Arbre filtré (déplacement dossier : on retire le dossier et ses descendants).
 */
const getFilteredFolderTree = computed((): FolderForDialog[] => {
    if (props.mode !== "folder" || !props.fromFolderId) {
        return rawFolderTree.value
    }
    const subtree = collectSubtreeIds(rawFolderTree.value, props.fromFolderId)
    if (!subtree) return rawFolderTree.value
    return filterFolderTreeByIds(rawFolderTree.value, subtree)
})

const getTableValue = computed((): FolderMoveTreeNode[] => {
    const wrapped = wrapFolderTreeWithWorkspaceRoot(getFilteredFolderTree.value)
    return wrapped
})

/**
 * Dossier cible choisi (`null` = racine « Mon espace »).
 */
const getSelectedTargetFolderId = computed((): string | null => {
    const row = selectedRows.value[0]
    if (!row?.data) return null
    return normalizeFolderId(row.data._id_folder as string | null)
})

/**
 * Sélection invalide ou déplacement sans effet.
 */
const getConfirmDisabled = computed(() => {
    if (selectedRows.value.length === 0) return true
    const target = getSelectedTargetFolderId.value
    if (props.mode === "building") {
        return normalizeFolderId(props.sourceFolderId ?? undefined) === target
    }
    return normalizeFolderId(props.sourceParentFolderId ?? undefined) === target
})

// ================================
// INTERACTIONS
// ================================
/**
 * Charge l’arborescence dossiers via l’API métier.
 */
async function loadFolderTree(): Promise<void> {
    loadingTree.value = true
    try {
        const data = await $getFolderTree()
        rawFolderTree.value = Array.isArray(data)
            ? (data as FolderForDialog[])
            : []
    } finally {
        loadingTree.value = false
    }
}

function onClose(): void {
    emit("update:visible", false)
}

/**
 * Confirme le déplacement (store + toasts gérés côté store).
 */
async function onConfirmMove(): Promise<void> {
    if (getConfirmDisabled.value) return
    const target = getSelectedTargetFolderId.value
    loadingMove.value = true
    try {
        if (props.mode === "building") {
            const ids = props.buildingIds ?? []
            if (!ids.length) return
            await homeWorkspace.submitMoveFolderBuilding(ids, target)
        } else if (props.fromFolderId) {
            await homeWorkspace.submitMoveFolder(
                props.fromFolderId,
                target,
                false
            )
        }
    } finally {
        loadingMove.value = false
    }
}

function resetLocalState(): void {
    selectedRows.value = []
    rawFolderTree.value = []
    treeExpandedKeys.value = { [WORKSPACE_ROOT_TREE_KEY]: true }
}

// ================================
// WATCHERS
// ================================
watch(
    () => props.visible,
    (v) => {
        if (v) {
            resetLocalState()
            void loadFolderTree()
        } else {
            resetLocalState()
        }
    }
)
</script>

<template>
    <!-- ================================ -->
    <!-- DIALOGUE — DÉPLACER BÂTIMENT / DOSSIER -->
    <!-- ================================ -->
    <BaseDialog
        :visible="visible"
        modal
        :header="getDialogHeader"
        class="w-[min(100%,42rem)] max-h-[90vh]"
        :breakpoints="{ '575px': '95vw' }"
        @update:visible="(v: boolean) => !v && onClose()"
    >
        <!-- ================================ -->
        <!-- CONTENU — ARBRE DOSSIERS -->
        <!-- ================================ -->
        <div class="flex flex-col gap-3 py-1">
            <p class="m-0 text-sm text-surface-600">
                Sélectionnez le dossier de destination. « Mon espace »
                correspond à la racine (hors sous-dossier).
            </p>
            <div
                class="max-h-[min(420px,55vh)] overflow-auto rounded-md border border-surface-200"
            >
                <BaseTableTree
                    v-model:selection="selectedRows"
                    v-model:expanded-keys="treeExpandedKeys"
                    :value="getTableValue"
                    :columns="getTableColumns"
                    data-key="key"
                    selection-mode="single"
                    :loading="loadingTree"
                    empty-message="Aucun dossier disponible"
                    scrollable
                    search
                    size="small"
                    selectable
                />
            </div>
        </div>
        <!-- ================================ -->
        <!-- ACTIONS -->
        <!-- ================================ -->
        <template #footer>
            <BaseButton label="Annuler" severity="secondary" @click="onClose" />
            <BaseButton
                label="Déplacer"
                :loading="loadingMove"
                :disabled="getConfirmDisabled || loadingTree"
                @click="onConfirmMove"
            />
        </template>
    </BaseDialog>
</template>
